import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

/**
 * Which order-entry layout a staff member uses.
 *
 * Deliberately separate from appearance. Appearance is cosmetic; this changes
 * how a counter task is performed, so it is chosen and measured on its own —
 * someone may want the new palette without the new form, or the reverse.
 *
 *   classic — today's two-column form. The default: nobody is moved off the
 *             layout they know without opting in.
 *   focused — the redesign's single column with a running summary alongside.
 *
 * NOT A TRUE A/B TEST, and worth being clear about. Staff self-select, so the
 * timing data that accompanies this shows preference and speed together and
 * cannot separate them. At a few dozen orders a day, random assignment would
 * take months to reach significance and would strand people on a layout they
 * dislike mid-rush; measured choice answers the question sooner and more
 * humanely, as long as nobody reads the result as causation.
 *
 * Stored per user, like appearance — terminals are shared.
 */

export const ORDER_LAYOUTS = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Two columns, every section visible at once, with the summary alongside.',
  },
  {
    id: 'focused',
    name: 'Focused',
    description: 'One column, worked top to bottom, with a running summary alongside.',
  },
];

export const DEFAULT_ORDER_LAYOUT = 'classic';

const DEVICE_KEY = 'twigs_order_layout';
const userKey = (userId) => `${DEVICE_KEY}:${userId}`;

const isValid = (value) => ORDER_LAYOUTS.some((l) => l.id === value);

const readStored = (userId) => {
  try {
    if (userId) {
      const forUser = localStorage.getItem(userKey(userId));
      return isValid(forUser) ? forUser : DEFAULT_ORDER_LAYOUT;
    }
    const forDevice = localStorage.getItem(DEVICE_KEY);
    if (isValid(forDevice)) return forDevice;
  } catch {
    /* private mode — fall through */
  }
  return DEFAULT_ORDER_LAYOUT;
};

const OrderLayoutContext = createContext({
  orderLayout: DEFAULT_ORDER_LAYOUT,
  setOrderLayout: () => {},
  orderLayouts: ORDER_LAYOUTS,
});

export const useOrderLayout = () => useContext(OrderLayoutContext);

export const OrderLayoutProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [orderLayout, setOrderLayoutState] = useState(() => readStored(null));

  const setOrderLayout = useCallback(
    (next) => {
      if (!isValid(next)) return;
      setOrderLayoutState(next);
      try {
        localStorage.setItem(DEVICE_KEY, next);
        if (userId) localStorage.setItem(userKey(userId), next);
      } catch {
        /* non-persistent beats not switching */
      }
    },
    [userId]
  );

  useEffect(() => {
    setOrderLayoutState(readStored(userId));
  }, [userId]);

  return (
    <OrderLayoutContext.Provider value={{ orderLayout, setOrderLayout, orderLayouts: ORDER_LAYOUTS }}>
      {children}
    </OrderLayoutContext.Provider>
  );
};

export default OrderLayoutContext;
